import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import UniversityManagerLayout from "../../components/layout/UniversityManagerLayout";
import { useTenant } from "../../context/TenantContext";
import umService from "../../services/university-admin/umService";
import { isMockDataUM } from "../../config/featureFlags";
import { useNotification } from "../../context/NotificationContext";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";
import PermissionGuard from "../../components/common/PermissionGuard";
import { uploadPhoto as uploadUMPhoto } from "../../services/university-admin/api";
import optimizeImageFile from "../../utils/imageOptimizer";

const UMUniversityInfo = () => {
  const { t } = useTranslation("universityManager");
  const { t: tCommon } = useTranslation("common");
  const { can } = useUniAdminAuth();
  const { currentTenantId } = useTenant();
  const { showSuccess, showError } = useNotification();
  const [university, setUniversity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  // Logo upload state
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const canUpdateLogo = can(PERMISSIONS.university.updateLogo);

  useEffect(() => {
    fetchUniversity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenantId]);

  const fetchUniversity = async () => {
    setLoading(true);
    const res = await umService.getUniversity(currentTenantId);
    if (res.success) {
      setUniversity(res.data);
      setFormData(res.data);
    }
    setLoading(false);
  };

  const handleEdit = () => {
    setFormData(university);
    setShowForm(true);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setFormData(university);
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await umService.updateUniversity(currentTenantId, formData);
      if (res.success) {
        // Merge updated fields back; the real API returns the full university object
        setUniversity((prev) => ({ ...prev, ...res.data }));
        setShowForm(false);
        showSuccess(t("universityInfo.form.updateSuccess"));
      } else {
        showError(t("universityInfo.form.updateFailed"));
      }
    } catch {
      showError(t("universityInfo.form.updateFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showError(t("universityInfo.uploadLogo.invalidType"));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showError(t("universityInfo.uploadLogo.tooLarge"));
      return;
    }
    setSelectedLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async () => {
    if (!selectedLogoFile) return;
    setUploadingLogo(true);
    try {
      // 1. Upload the image (POST /photos/upload) to get photoId
      const fileToSend = await optimizeImageFile(selectedLogoFile, { fillWhiteBackground: true });
      const uploadRes = await uploadUMPhoto(fileToSend);

      const photoId =
        uploadRes?.data?.id ||
        uploadRes?.data?.photoId ||
        uploadRes?.data?.imageId ||
        uploadRes?.id ||
        uploadRes?.photoId;

      if (!photoId) {
        showError(t("universityInfo.uploadLogo.uploadFailed"));
        setUploadingLogo(false);
        return;
      }

      // 2. Link the logo to the university (PUT /university-admin/university/me/logo)
      const linkRes = await umService.updateUniversityLogo(photoId);

      if (linkRes.success !== false) {
        showSuccess(t("universityInfo.uploadLogo.success"));
        setSelectedLogoFile(null);
        setLogoPreview(null);
        // 3. Refresh university info to get updated logoUrl and logoPhotoId
        fetchUniversity();
      } else {
        showError(t("universityInfo.uploadLogo.updateFailed"));
      }
    } catch {
      showError(t("universityInfo.uploadLogo.updateFailed"));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoCancel = () => {
    setSelectedLogoFile(null);
    setLogoPreview(null);
  };

  const isActive = university?.active ?? university?.status;

  if (loading) {
    return (
      <UniversityManagerLayout>
        <div className="university-info-page">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{tCommon("actions.loading")}</span>
            </div>
          </div>
        </div>
      </UniversityManagerLayout>
    );
  }

  if (!university) {
    return (
      <UniversityManagerLayout>
        <div className="university-info-page">
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {t("universityInfo.noDataFound")}
          </div>
        </div>
      </UniversityManagerLayout>
    );
  }

  const displayLogoUrl = logoPreview || university.logoUrl;

  return (
    <UniversityManagerLayout>
      <PermissionGuard allow={can(PERMISSIONS.university.get)}>
      <div className="university-info-page">
        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1 fw-bold text-dark">{t("universityInfo.title")}</h1>
            <p className="text-muted mb-0">{t("universityInfo.subtitle")}</p>
          </div>
          {can(PERMISSIONS.university.updateLoginPolicy) && (
            <button className="btn btn-primary" onClick={handleEdit}>
              <i className="fas fa-edit me-2"></i>
              {t("universityInfo.editButton")}
            </button>
          )}
        </div>

        {/* University Details Card */}
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            {/* University Header */}
            <div className="d-flex align-items-center mb-4 pb-4 border-bottom">
              <div className="position-relative me-3">
                <div
                  className="rounded-4 bg-white p-2 d-flex align-items-center justify-content-center border shadow-sm"
                  style={{ width: "85px", height: "85px" }}
                >
                  {displayLogoUrl ? (
                    <img src={displayLogoUrl} alt={university.nameEn} className="w-100 h-100"
                      style={{ objectFit: "contain", backgroundColor: "#ffffff" }} />
                  ) : (
                    <i className="fas fa-university text-primary fs-1"></i>
                  )}
                </div>
                {canUpdateLogo && (
                  <label
                    htmlFor="header-logo-input"
                    className="btn btn-sm btn-primary rounded-circle position-absolute bottom-0 end-0 p-0 d-flex align-items-center justify-content-center shadow"
                    style={{ width: "30px", height: "30px", cursor: "pointer" }}
                    title={t("universityInfo.uploadLogo.changeLogo")}
                  >
                    <i className="fas fa-camera fs-6"></i>
                    <input
                      type="file"
                      id="header-logo-input"
                      accept="image/*"
                      className="d-none"
                      onChange={handleLogoSelect}
                      disabled={uploadingLogo}
                    />
                  </label>
                )}
              </div>
              <div>
                <h3 className="fw-bold text-dark mb-1">{university.nameEn}</h3>
                <p className="text-muted mb-2">{university.nameLocal}</p>
                <div className="d-flex gap-2 flex-wrap">
                  {university.shortName && (
                    <span className="badge bg-primary bg-opacity-10 text-primary">{university.shortName}</span>
                  )}
                  {university.mailPattern && (
                    <span className="badge bg-secondary bg-opacity-10 text-secondary">
                      <i className="fas fa-envelope me-1"></i>{university.mailPattern}
                    </span>
                  )}
                  <span className={`badge ${isActive ? "bg-success" : "bg-danger"} bg-opacity-10 text-${isActive ? "success" : "danger"} fw-bold`}>
                    <i className={`fas ${isActive ? "fa-check-circle" : "fa-times-circle"} me-1`}></i>
                    {isActive ? t("universityInfo.status.active") : t("universityInfo.status.inactive")}
                  </span>
                </div>
              </div>
            </div>

            {/* Upload Logo Section (Gated by university:updateLogo) */}
            {canUpdateLogo && (
              <div className="form-section mb-4 p-3 bg-light rounded border">
                <div className="form-section-header mb-3 d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-image text-primary me-2"></i>
                    <span className="fw-bold">{t("universityInfo.uploadLogo.sectionTitle")}</span>
                  </div>
                </div>

                <div className="row align-items-center g-3">
                  <div className="col-md-7">
                    <div className="input-group">
                      <input
                        type="file"
                        id="university-logo-upload-section"
                        accept="image/*"
                        className="form-control"
                        onChange={handleLogoSelect}
                        disabled={uploadingLogo}
                      />
                      {selectedLogoFile && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={handleLogoCancel}
                          disabled={uploadingLogo}
                        >
                          <i className="fas fa-times me-1"></i>
                          {t("universityInfo.uploadLogo.cancel")}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleLogoUpload}
                        disabled={!selectedLogoFile || uploadingLogo}
                      >
                        {uploadingLogo ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {t("universityInfo.uploadLogo.uploading")}
                          </>
                        ) : (
                          <>
                            <i className="fas fa-cloud-upload-alt me-2"></i>
                            {t("universityInfo.uploadLogo.uploadButton")}
                          </>
                        )}
                      </button>
                    </div>
                    <small className="text-muted mt-1 d-block">
                      {t("universityInfo.uploadLogo.hint")}
                    </small>
                  </div>

                  {selectedLogoFile && (
                    <div className="col-md-5 d-flex align-items-center gap-2">
                      <span className="badge bg-info bg-opacity-10 text-info fw-normal text-truncate" style={{ maxWidth: "200px" }}>
                        <i className="fas fa-file-image me-1"></i>
                        {selectedLogoFile.name}
                      </span>
                      <small className="text-muted">
                        ({(selectedLogoFile.size / 1024).toFixed(1)} KB)
                      </small>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Basic Information (mock has more fields; real API has nameEn, nameLocal, mailPattern) */}
            <div className="form-section mb-4">
              <div className="form-section-header mb-3">
                <i className="fas fa-info-circle text-primary"></i>
                <span>{t("universityInfo.sections.basicInformation")}</span>
              </div>
              <div className="row g-4">
                <div className="col-md-6">
                  <label className="form-label fw-bold text-muted small">{t("universityInfo.fields.englishName")}</label>
                  <p className="text-dark mb-0">{university.nameEn || "—"}</p>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold text-muted small">{t("universityInfo.fields.localName")}</label>
                  <p className="text-dark mb-0">{university.nameLocal || "—"}</p>
                </div>
                {university.shortName && (
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-muted small">{t("universityInfo.fields.shortName")}</label>
                    <p className="text-dark mb-0">{university.shortName}</p>
                  </div>
                )}
                {university.foundedYear && (
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-muted small">{t("universityInfo.fields.foundedYear")}</label>
                    <p className="text-dark mb-0">{university.foundedYear}</p>
                  </div>
                )}
                {university.mailPattern && (
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-muted small">{t("universityInfo.fields.mailPattern")}</label>
                    <p className="text-dark mb-0">{university.mailPattern}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Login Policy — always visible (real API: loginEnabled, registrationEnabled, maintenanceMode) */}
            <div className="form-section mb-4">
              <div className="form-section-header mb-3">
                <i className="fas fa-shield-alt text-primary"></i>
                <span>{t("universityInfo.sections.loginPolicy")}</span>
              </div>
              <div className="row g-4">
                <div className="col-md-4">
                  <label className="form-label fw-bold text-muted small">{t("universityInfo.loginPolicy.loginEnabled")}</label>
                  <p className="text-dark mb-0">
                    {university.loginEnabled
                      ? <span className="badge bg-success bg-opacity-10 text-success"><i className="fas fa-check me-1"></i>{t("universityInfo.loginPolicy.yes")}</span>
                      : <span className="badge bg-danger bg-opacity-10 text-danger"><i className="fas fa-times me-1"></i>{t("universityInfo.loginPolicy.no")}</span>}
                  </p>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold text-muted small">{t("universityInfo.loginPolicy.registrationEnabled")}</label>
                  <p className="text-dark mb-0">
                    {university.registrationEnabled
                      ? <span className="badge bg-success bg-opacity-10 text-success"><i className="fas fa-check me-1"></i>{t("universityInfo.loginPolicy.yes")}</span>
                      : <span className="badge bg-danger bg-opacity-10 text-danger"><i className="fas fa-times me-1"></i>{t("universityInfo.loginPolicy.no")}</span>}
                  </p>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold text-muted small">{t("universityInfo.loginPolicy.maintenanceMode")}</label>
                  <p className="text-dark mb-0">
                    {university.maintenanceMode
                      ? <span className="badge bg-warning bg-opacity-10 text-warning"><i className="fas fa-tools me-1"></i>{t("universityInfo.loginPolicy.modeActive")}</span>
                      : <span className="badge bg-success bg-opacity-10 text-success"><i className="fas fa-check me-1"></i>{t("universityInfo.loginPolicy.modeOff")}</span>}
                  </p>
                </div>
              </div>
            </div>

            {/* Contact & Location — only shown in mock mode where this data is available */}
            {isMockDataUM && (
              <>
                <div className="form-section mb-4">
                  <div className="form-section-header mb-3">
                    <i className="fas fa-address-book text-primary"></i>
                    <span>{t("universityInfo.sections.contactInformation")}</span>
                  </div>
                  <div className="row g-4">
                    <div className="col-md-6">
                      <label className="form-label fw-bold text-muted small">{t("universityInfo.fields.email")}</label>
                      <p className="text-dark mb-0">
                        {university.email ? <a href={`mailto:${university.email}`} className="text-decoration-none"><i className="fas fa-envelope me-2"></i>{university.email}</a> : "—"}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold text-muted small">{t("universityInfo.fields.phone")}</label>
                      <p className="text-dark mb-0">{university.phone ? <><i className="fas fa-phone me-2"></i>{university.phone}</> : "—"}</p>
                    </div>
                    <div className="col-md-12">
                      <label className="form-label fw-bold text-muted small">{t("universityInfo.fields.website")}</label>
                      <p className="text-dark mb-0">
                        {university.website ? <a href={university.website} target="_blank" rel="noopener noreferrer" className="text-decoration-none"><i className="fas fa-globe me-2"></i>{university.website}</a> : "—"}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="form-section mb-4">
                  <div className="form-section-header mb-3">
                    <i className="fas fa-map-marker-alt text-primary"></i>
                    <span>{t("universityInfo.sections.locationInformation")}</span>
                  </div>
                  <div className="row g-4">
                    <div className="col-md-6">
                      <label className="form-label fw-bold text-muted small">{t("universityInfo.fields.city")}</label>
                      <p className="text-dark mb-0">{university.cityName || "—"}</p>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold text-muted small">{t("universityInfo.fields.country")}</label>
                      <p className="text-dark mb-0">{university.countryName || "—"}</p>
                    </div>
                    <div className="col-md-12">
                      <label className="form-label fw-bold text-muted small">{t("universityInfo.fields.address")}</label>
                      <p className="text-dark mb-0">{university.address || "—"}</p>
                    </div>
                  </div>
                </div>
                {(university.descriptionEn || university.descriptionLocal) && (
                  <div className="form-section">
                    <div className="form-section-header mb-3">
                      <i className="fas fa-align-left text-primary"></i>
                      <span>{t("universityInfo.sections.description")}</span>
                    </div>
                    <div className="row g-4">
                      {university.descriptionEn && (
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">{t("universityInfo.fields.englishDescription")}</label>
                          <p className="text-dark mb-0">{university.descriptionEn}</p>
                        </div>
                      )}
                      {university.descriptionLocal && (
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">{t("universityInfo.fields.localDescription")}</label>
                          <p className="text-dark mb-0">{university.descriptionLocal}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Edit Form Modal */}
        {showForm && (
          <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-edit me-2"></i>
                    {t("universityInfo.editModalTitle")}
                  </h5>
                  <button type="button" className="btn-close" onClick={handleFormCancel} disabled={saving}></button>
                </div>
                <form onSubmit={handleFormSubmit}>
                  <div className="modal-body">
                    {isMockDataUM ? (
                      /* Full edit form for mock mode */
                      <>
                        <div className="form-section mb-4">
                          <div className="form-section-header mb-3">
                            <i className="fas fa-info-circle text-primary"></i>
                            <span>{t("universityInfo.sections.basicInformation")}</span>
                          </div>
                          <div className="row g-3">
                            <div className="col-md-6">
                              <label className="form-label fw-bold">{t("universityInfo.form.englishName")} <span className="text-danger">*</span></label>
                              <input type="text" name="nameEn" className="form-control" value={formData.nameEn || ""} onChange={handleChange} required />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label fw-bold">{t("universityInfo.form.localName")} <span className="text-danger">*</span></label>
                              <input type="text" name="nameLocal" className="form-control" value={formData.nameLocal || ""} onChange={handleChange} required />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label fw-bold">{t("universityInfo.form.shortName")} <span className="text-danger">*</span></label>
                              <input type="text" name="shortName" className="form-control" value={formData.shortName || ""} onChange={handleChange} required />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label fw-bold">{t("universityInfo.form.foundedYear")}</label>
                              <input type="number" name="foundedYear" className="form-control" value={formData.foundedYear || ""} onChange={handleChange} min="1000" max="2100" />
                            </div>
                          </div>
                        </div>
                        <div className="form-section mb-4">
                          <div className="form-section-header mb-3">
                            <i className="fas fa-address-book text-primary"></i>
                            <span>{t("universityInfo.sections.contactInformation")}</span>
                          </div>
                          <div className="row g-3">
                            <div className="col-md-6">
                              <label className="form-label fw-bold">{t("universityInfo.form.email")} <span className="text-danger">*</span></label>
                              <input type="email" name="email" className="form-control" value={formData.email || ""} onChange={handleChange} required />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label fw-bold">{t("universityInfo.form.phone")}</label>
                              <input type="tel" name="phone" className="form-control" value={formData.phone || ""} onChange={handleChange} />
                            </div>
                            <div className="col-md-12">
                              <label className="form-label fw-bold">{t("universityInfo.form.website")}</label>
                              <input type="url" name="website" className="form-control" value={formData.website || ""} onChange={handleChange} placeholder={t("universityInfo.form.websitePlaceholder")} />
                            </div>
                          </div>
                        </div>
                        <div className="form-section mb-4">
                          <div className="form-section-header mb-3">
                            <i className="fas fa-map-marker-alt text-primary"></i>
                            <span>{t("universityInfo.sections.locationInformation")}</span>
                          </div>
                          <div className="row g-3">
                            <div className="col-md-6">
                              <label className="form-label fw-bold">{t("universityInfo.form.country")} <span className="text-danger">*</span></label>
                              <input type="text" name="countryName" className="form-control" value={formData.countryName || ""} onChange={handleChange} required />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label fw-bold">{t("universityInfo.form.city")} <span className="text-danger">*</span></label>
                              <input type="text" name="cityName" className="form-control" value={formData.cityName || ""} onChange={handleChange} required />
                            </div>
                            <div className="col-md-12">
                              <label className="form-label fw-bold">{t("universityInfo.form.address")}</label>
                              <input type="text" name="address" className="form-control" value={formData.address || ""} onChange={handleChange} />
                            </div>
                          </div>
                        </div>
                        <div className="form-section">
                          <div className="form-section-header mb-3">
                            <i className="fas fa-align-left text-primary"></i>
                            <span>{t("universityInfo.sections.description")}</span>
                          </div>
                          <div className="row g-3">
                            <div className="col-md-6">
                              <label className="form-label fw-bold">{t("universityInfo.form.englishDescription")}</label>
                              <textarea name="descriptionEn" className="form-control" rows={4} value={formData.descriptionEn || ""} onChange={handleChange} placeholder={t("universityInfo.form.englishDescriptionPlaceholder")} />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label fw-bold">{t("universityInfo.form.localDescription")}</label>
                              <textarea name="descriptionLocal" className="form-control" rows={4} value={formData.descriptionLocal || ""} onChange={handleChange} placeholder={t("universityInfo.form.localDescriptionPlaceholder")} />
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      /* Simplified edit form for real API — only login policy is updatable */
                      <div className="form-section">
                        <div className="alert alert-info mb-4">
                          <i className="fas fa-info-circle me-2"></i>
                          {t("universityInfo.loginPolicy.onlyPolicyNote")}
                        </div>
                        <div className="form-section-header mb-3">
                          <i className="fas fa-shield-alt text-primary"></i>
                          <span>{t("universityInfo.sections.loginPolicy")}</span>
                        </div>
                        <div className="row g-4">
                          <div className="col-md-4">
                            <div className="form-check form-switch">
                              <input className="form-check-input" type="checkbox" role="switch" id="loginEnabled" name="loginEnabled"
                                checked={!!formData.loginEnabled} onChange={handleChange} />
                              <label className="form-check-label fw-bold" htmlFor="loginEnabled">{t("universityInfo.loginPolicy.loginEnabled")}</label>
                            </div>
                            <small className="text-muted">{t("universityInfo.loginPolicy.loginEnabledDesc")}</small>
                          </div>
                          <div className="col-md-4">
                            <div className="form-check form-switch">
                              <input className="form-check-input" type="checkbox" role="switch" id="registrationEnabled" name="registrationEnabled"
                                checked={!!formData.registrationEnabled} onChange={handleChange} />
                              <label className="form-check-label fw-bold" htmlFor="registrationEnabled">{t("universityInfo.loginPolicy.registrationEnabled")}</label>
                            </div>
                            <small className="text-muted">{t("universityInfo.loginPolicy.registrationEnabledDesc")}</small>
                          </div>
                          <div className="col-md-4">
                            <div className="form-check form-switch">
                              <input className="form-check-input" type="checkbox" role="switch" id="maintenanceMode" name="maintenanceMode"
                                checked={!!formData.maintenanceMode} onChange={handleChange} />
                              <label className="form-check-label fw-bold" htmlFor="maintenanceMode">{t("universityInfo.loginPolicy.maintenanceMode")}</label>
                            </div>
                            <small className="text-muted">{t("universityInfo.loginPolicy.maintenanceModeDesc")}</small>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={handleFormCancel} disabled={saving}>
                      {tCommon("buttons.cancel")}
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? (
                        <><span className="spinner-border spinner-border-sm me-2" role="status"></span>{t("universityInfo.form.saving")}</>
                      ) : (
                        <><i className="fas fa-save me-2"></i>{t("universityInfo.form.saveChanges")}</>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
      </PermissionGuard>
    </UniversityManagerLayout>
  );
};

export default UMUniversityInfo;
